import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Icon } from '../core/Icon';

export interface Comando {
    id: string;
    label: string;
    icon?: string;
    group?: ReactNode;
    shortcut?: string;
    /** Marca o comando como ação de IA, que recebe o acento de marca. */
    ai?: boolean;
}

export interface CommandPaletteProps {
    open?: boolean;
    commands?: Comando[];
    onClose?: () => void;
    onRun?: (id: string) => void;
    placeholder?: string;
}

export function CommandPalette({
    open = false, commands = [], onClose, onRun, placeholder = 'Buscar ou digitar um comando…',
}: CommandPaletteProps) {
    const [q, setQ] = useState('');
    const [sel, setSel] = useState(0);
    const campoRef = useRef<HTMLInputElement | null>(null);
    const lista = commands.filter((c) => c.label.toLowerCase().includes(q.toLowerCase()));

    useEffect(() => { setSel(0); }, [q, open]);

    /* Foco por efeito, não por `autoFocus`: o atributo é aplicado uma vez na
       montagem e a regra de acessibilidade o reprova por roubar o foco em
       contexto onde a pessoa não pediu. Aqui a paleta só existe quando foi
       aberta de propósito, e o foco precisa reentrar a cada abertura. */
    useEffect(() => {
        if (open) campoRef.current?.focus();
    }, [open]);

    if (!open) return null;

    const executar = (id: string) => {
        onRun?.(id);
        onClose?.();
    };

    return (
        <div
            role="presentation"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(17,19,24,.32)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '12vh', zIndex: 60 }}
        >
            <div
                role="dialog" aria-modal="true" aria-label="Paleta de comandos"
                style={{ width: 560, maxWidth: '92vw', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-panel)', boxShadow: 'var(--shadow-xl)', overflow: 'hidden' }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 14px', height: 50, borderBottom: '1px solid var(--border-subtle)' }}>
                    <Icon name="search" size={17} style={{ color: 'var(--neutral-500)' }} />
                    <input
                        ref={campoRef}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder={placeholder}
                        aria-label={placeholder}
                        onKeyDown={(e) => {
                            if (e.key === 'ArrowDown') setSel((s) => Math.min(s + 1, lista.length - 1));
                            if (e.key === 'ArrowUp') setSel((s) => Math.max(s - 1, 0));
                            if (e.key === 'Enter') {
                                const alvo = lista[sel];
                                if (alvo) executar(alvo.id);
                            }
                            if (e.key === 'Escape') onClose?.();
                        }}
                        style={{ flex: 1, border: 0, outline: 'none', fontFamily: 'var(--font-ui)', fontSize: 15, color: 'var(--text-primary)', background: 'transparent' }}
                    />
                    <kbd style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-tertiary)', border: '1px solid var(--border)', borderRadius: 4, padding: '2px 6px' }}>ESC</kbd>
                </div>
                <div style={{ maxHeight: 320, overflowY: 'auto', padding: 6 }}>
                    {lista.length === 0 && (
                        <p style={{ margin: 0, padding: '24px 14px', fontSize: 13.5, color: 'var(--text-secondary)', textAlign: 'center' }}>Nenhum comando encontrado.</p>
                    )}
                    {lista.map((c, i) => (
                        <button
                            key={c.id} type="button"
                            onMouseEnter={() => setSel(i)}
                            onClick={() => executar(c.id)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 11, width: '100%', padding: '9px 10px', border: 0,
                                background: i === sel ? 'var(--surface-secondary)' : 'transparent', borderRadius: 'var(--radius-xs)',
                                cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-ui)', fontSize: 13.5, color: 'var(--text-primary)',
                            }}
                        >
                            <Icon name={c.icon ?? 'circle'} size={16} style={{ color: c.ai ? 'var(--burgundy-500)' : 'var(--neutral-500)' }} />
                            <span style={{ flex: 1 }}>{c.label}</span>
                            {c.group && <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{c.group}</span>}
                            {c.shortcut && <kbd style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-tertiary)' }}>{c.shortcut}</kbd>}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
